<script>
import ApplicantLayout from "../../layouts/applicant";
import AppIcon from "@/components/shared/AppIcon.vue";
import TableFooter from "@/components/shared/TableFooter.vue";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import axios from "axios";
import storageService from "@/services/storage.service";
import { formatDate } from "@/helpers/dateFormatter";

export default {
  name: "ApplicantViewLog",
  components: { ApplicantLayout, AppIcon, EasyDataTable, TableFooter },
  data() {
    return {
      isLoading: false,
      loadError: false,
      searchValueTable: "",
      headers: [
        { text: "Name", value: "staffname", sortable: true },
        { text: "Date & Time", value: "logdate", sortable: true },
        { text: "Activity", value: "appactivity" },
      ],
      items: [],
      // all logs are loaded at once, so search, sort and paging happen here and
      // the same TableFooter as every other list page is used
      serverOptions: { page: 1, rowsPerPage: 10, sortBy: null, sortType: null },
      appId: null, // the applicant's submitted application (opened read-only)
    };
  },
  computed: {
    // keeps the template and the old `arrItem` name in sync
    arrItem() {
      return this.items;
    },
    filteredItems() {
      const q = String(this.searchValueTable || "").trim().toLowerCase();
      if (!q) return this.items;
      return this.items.filter((i) =>
        [i.staffname, i.logdate, i.appactivity].some((v) =>
          String(v || "").toLowerCase().includes(q),
        ),
      );
    },
    sortedItems() {
      const { sortBy, sortType } = this.serverOptions;
      if (!sortBy) return this.filteredItems;
      const dir = sortType === "desc" ? -1 : 1;
      const key =
        sortBy === "logdate"
          ? (i) => i.rawDate
          : (i) => String(i.staffname || "").toLowerCase();
      return [...this.filteredItems].sort((a, b) => {
        const x = key(a);
        const y = key(b);
        return x < y ? -dir : x > y ? dir : 0;
      });
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.serverOptions.rowsPerPage));
    },
    // never point past the last page after a search shrinks the list
    currentPage() {
      return Math.min(this.serverOptions.page, this.pageCount);
    },
    pagedItems() {
      const size = this.serverOptions.rowsPerPage;
      const start = (this.currentPage - 1) * size;
      return this.sortedItems.slice(start, start + size);
    },
  },
  watch: {
    searchValueTable() {
      this.serverOptions = { ...this.serverOptions, page: 1 };
    },
  },
  mounted() {
    this.fetchLogs();
    this.fetchApplication();
  },
  methods: {
    // Finds the applicant's own latest application so it can be opened from this
    // page. The dashboard endpoint is used because it returns the latest
    // application in ANY status (applicant_app_status hides approved / rejected
    // ones, which would remove the button exactly when the applicant wants it).
    fetchApplication() {
      axios
        .post("/applications/applicant_dashboard/", {}, { loader: false })
        .then((response) => {
          const app = response.data && response.data.user_data;
          this.appId = app && app.id ? app.id : null;
        })
        .catch(() => {
          this.appId = null; // the button simply stays hidden
        });
    },
    fetchLogs() {
      this.isLoading = true;
      this.loadError = false;
      axios
        .get("/accounts/get_logs/", {
          params: { access_token: storageService.getItem("accessToken") },
        })
        .then((response) => {
          const rawLogs = response.data?.log_data || response.data?.results || [];
          this.items = rawLogs.map((log) => {
            const user = log.user || {};
            const staffname =
              log.staffname ||
              (user.first_name || user.last_name
                ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                : user.username || "System");
            const rawDate =
              log.created_on ||
              log.created_at ||
              (log.dateLog ? `${log.dateLog} ${log.timeLog || ""}` : "");
            const formatted = formatDate(rawDate);
            return {
              id: log.id,
              staffname,
              logdate: formatted,
              dateLog: formatted,
              rawDate: Date.parse(rawDate) || 0, // for sorting by date
              appactivity: log.appactivity || log.log || "Activity logged",
            };
          });
        })
        .catch((error) => {
          console.error("Failed to load application logs:", error);
          this.loadError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onRowsChange(rowsPerPage) {
      this.serverOptions = { ...this.serverOptions, rowsPerPage, page: 1 };
    },
    initials(name) {
      return (
        String(name || "")
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((p) => p[0].toUpperCase())
          .join("") || "?"
      );
    },
  },
};
</script>

<template>
  <ApplicantLayout>
    <section class="logs">
      <header class="ecu-page-head">
        <div>
          <h1>Application Status Log</h1>
          <p class="ecu-page-head__sub">Every update made to your application, in one place.</p>
        </div>

        <div class="logs__tools">
          <label class="logs__search">
            <AppIcon name="search" :size="15" />
            <input
              v-model="searchValueTable"
              type="text"
              placeholder="Search logs..."
              aria-label="Search logs"
              autocomplete="off"
            />
            <button
              v-if="searchValueTable"
              type="button"
              class="logs__clear"
              aria-label="Clear search"
              @click="searchValueTable = ''"
            >
              <AppIcon name="x" :size="14" :stroke="2.4" />
            </button>
          </label>
          <router-link
            v-if="appId"
            :to="{ name: 'my_application', params: { id: appId } }"
            class="ecu-btn-solid"
          >
            <AppIcon name="file" :size="14" /> View Application
          </router-link>
        </div>
      </header>

      <div v-if="loadError" class="ecu-card">
        <div class="ecu-state">
          <span class="ecu-state__icon ecu-state__icon--danger"><AppIcon name="alert" :size="26" /></span>
          <h3>We couldn't load the application log</h3>
          <p>Please check your connection and try again.</p>
          <button type="button" class="ecu-btn-outline" @click="fetchLogs">
            <AppIcon name="refresh" :size="14" /> Try again
          </button>
        </div>
      </div>

      <div v-else class="ecu-card logs__card">
        <EasyDataTable
          header-text-direction="center"
          :headers="headers"
          :items="pagedItems"
          v-model:server-options="serverOptions"
          :server-items-length="filteredItems.length"
          :loading="isLoading"
          theme-color="#f97316"
          hide-footer
          table-class-name="ecu-log-table"
          empty-message="No activity recorded yet"
        >
          <template #item-staffname="{ staffname }">
            <span class="logs__person">
              <span class="logs__avatar">{{ initials(staffname) }}</span>
              {{ staffname }}
            </span>
          </template>
          <template #item-logdate="{ logdate, dateLog }">
            <span class="logs__date">
              <AppIcon name="clock" :size="13" /> {{ logdate || dateLog }}
            </span>
          </template>
          <template #item-appactivity="{ appactivity }">
            <span class="logs__activity">{{ appactivity }}</span>
          </template>
        </EasyDataTable>

        <TableFooter
          :page="currentPage"
          :rows-per-page="serverOptions.rowsPerPage"
          :total="filteredItems.length"
          @update:page="serverOptions = { ...serverOptions, page: $event }"
          @update:rowsPerPage="onRowsChange"
        />
      </div>
    </section>
  </ApplicantLayout>
</template>

<style scoped>
.logs__tools {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
/* same height as the search box so the two sit on one line */
.logs__tools .ecu-btn-solid {
  box-sizing: border-box;
  height: 38.4px;
  padding-top: 0;
  padding-bottom: 0;
  justify-content: center;
  white-space: nowrap;
}
.logs__search {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 256px;
  max-width: 100%;
  padding: 0 12.8px;
  height: 38.4px;
  border-radius: 11.2px;
  background: #fff;
  border: 1.5px solid var(--ecu-border);
  color: #8b90a3;
  box-shadow: 0 4.8px 16px rgba(20, 40, 90, 0.05);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.logs__search:focus-within {
  border-color: #ffb27a;
  box-shadow: 0 0 0 3.2px rgba(249, 115, 22, 0.12);
  color: var(--ecu-orange);
}
.logs__search input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  font: inherit;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-ink);
}
.logs__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-right: -6px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #5f6579;
  cursor: pointer;
}
.logs__clear:hover {
  color: #e84d00;
  background: #fff1e6;
}
.logs__clear:focus-visible {
  outline: 2px solid var(--ecu-orange);
  outline-offset: 1px;
}
.logs__search input::placeholder {
  color: #9aa0b4;
}
.logs__card {
  padding: 6.4px;
  overflow: hidden;
}
.logs__person {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--ecu-navy);
  white-space: nowrap;
}
.logs__avatar {
  width: 25.6px;
  height: 25.6px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ecu-fs-small);
  font-weight: 800;
  color: var(--ecu-orange-dark);
  background: var(--ecu-orange-soft);
}
.logs__date {
  display: inline-flex;
  align-items: center;
  gap: 5.6px;
  font-size: var(--ecu-fs-body);
  font-weight: 500;
  color: #3c4668;
  white-space: nowrap;
}
.logs__date .app-icon {
  color: #a3a9bd;
}
.logs__activity {
  font-weight: 500;
  color: var(--ecu-ink);
}

@media (max-width: 767px) {
  .logs__tools {
    width: 100%;
  }
  .logs__search {
    width: 100%;
  }
  .logs__tools .ecu-btn-solid {
    width: 100%;
  }
}
</style>

<!-- EasyDataTable renders its own DOM, so its theme lives in an unscoped block with a unique class -->
<style>
.ecu-log-table {
  --easy-table-border: 0;
  --easy-table-row-border: 1px solid #f0f1f7;

  --easy-table-header-font-size: var(--ecu-fs-body);
  --easy-table-header-height: 38.4px;
  --easy-table-header-font-color: #5f6579;
  --easy-table-header-background-color: #fff7f1;
  --easy-table-header-item-padding: 6.4px 14.4px;

  --easy-table-body-even-row-font-color: #1d2a4d;
  --easy-table-body-even-row-background-color: #fff;
  --easy-table-body-row-font-color: #1d2a4d;
  --easy-table-body-row-background-color: #fff;
  --easy-table-body-row-height: 46.4px;
  --easy-table-body-row-font-size: var(--ecu-fs-body);
  --easy-table-body-row-hover-font-color: #1d2a4d;
  --easy-table-body-row-hover-background-color: #fffaf5;
  --easy-table-body-item-padding: 6.4px 14.4px;

  --easy-table-footer-background-color: #fff;
  --easy-table-footer-font-color: #5f6579;
  --easy-table-footer-font-size: var(--ecu-fs-body);
  --easy-table-footer-padding: 0 14.4px;
  --easy-table-footer-height: 46.4px;

  --easy-table-rows-per-page-selector-width: 59.2px;
  --easy-table-rows-per-page-selector-option-padding: 6.4px;
  --easy-table-rows-per-page-selector-z-index: 5;

  --easy-table-loading-mask-background-color: #fff;
  font-family: inherit;
}
.ecu-log-table .vue3-easy-data-table__main {
  border-radius: 14.4px;
}
.ecu-log-table th {
  font-weight: 700 !important;
}
</style>